import React from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { Button } from '../../../../components/Button';
import { SensorAdvancedFormik } from './useSensorAdvancedForm';
import { Panel } from '../../../../components/Panel';
import { RouteLeavingGuard } from '../../../../components/RouteLeavingGuard';
import { ButtonGroup } from '../../../../components/ButtonGroup';
import { t } from '../../../../lib/i18n';
import { generateAutoCompleteValue } from '../../../../lib/utils/generateAutoCompleteValue';
import { SensorType } from '../../../../serverApi';

interface Props {
  form: SensorAdvancedFormik;
  onRebootRequest?: () => void;
  onResetRequest?: () => void;
  sensorType: SensorType;
}

const frequencyOptions = [
  {
    label: '13 MHz',
    value: 13000000,
  },
  {
    label: '14 MHz',
    value: 14000000,
  },
  {
    label: '15 MHz (standard)',
    value: 15000000,
  },
  {
    label: '16 MHz',
    value: 16000000,
  },
];

export const SensorAdvancedForm = (props: Props) => {
  const loading = props.form.isSubmitting;

  const isRoomMateSensor = props.sensorType === 'roommate';
  return (
    <div className="SensorAdvancedForm mt-3">
      <RouteLeavingGuard when={props.form.dirty} />
      <FormGroup className="mt-3">
        <FormControlLabel
          style={{ marginLeft: -8 }}
          control={
            <Checkbox
              color="primary"
              name="demo_mode"
              value="demo_mode"
              disabled={loading}
              checked={props.form.values.demo_mode}
              onChange={(e) => {
                props.form.setFieldValue('demo_mode', e.currentTarget.checked);
                props.form.setFieldTouched('demo_mode');
              }}
              onBlur={props.form.handleBlur}
            />
          }
          label={t(
            'manage.sensors.details.SensorAdvancedForm.markAsTestSensor'
          )}
        />
      </FormGroup>

      {isRoomMateSensor && (
        <>
          <FormGroup className="mt-3">
            <FormControl>
              <TextField
                select={true}
                disabled={loading}
                name="modulation_frequency"
                label={t(
                  'manage.sensors.details.SensorAdvancedForm.frequencyRange'
                )}
                value={props.form.values.modulation_frequency}
                onBlur={props.form.handleBlur}
                onChange={(e) => {
                  props.form.setFieldValue(
                    'modulation_frequency',
                    parseInt(e.target.value as string, 10)
                  );
                  props.form.setFieldTouched('modulation_frequency');
                }}
                error={props.form.errors.modulation_frequency !== undefined}
                helperText={props.form.errors.modulation_frequency}
                style={{ maxWidth: 400 }}
              >
                {frequencyOptions.map((fo) => (
                  <MenuItem key={fo.value} value={fo.value}>
                    {fo.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </FormGroup>

          <FormGroup className="mt-3">
            <FormControl>
              <TextField
                disabled={loading}
                name="sensor_algorithm_config_bits"
                label={t('manage.sensors.details.SensorAdvancedForm.sConf')}
                autoComplete={generateAutoCompleteValue()}
                value={props.form.values.sensor_algorithm_config_bits}
                onBlur={props.form.handleBlur}
                onChange={(e) => {
                  const val =
                    e.target.value === '0'
                      ? 0
                      : parseInt(e.target.value, 10) || e.target.value;

                  props.form.setFieldValue(e.target.name, val);
                  props.form.setFieldTouched(e.target.name);
                }}
                error={
                  props.form.errors.sensor_algorithm_config_bits !== undefined
                }
                helperText={props.form.errors.sensor_algorithm_config_bits}
                style={{ maxWidth: 400 }}
              />
            </FormControl>
          </FormGroup>
        </>
      )}
      <ButtonGroup direction="horizontal" className="mt-5">
        <Button
          type="submit"
          onClick={props.form.submitForm}
          color="primary"
          disabled={loading}
          icon={loading ? 'gear' : undefined}
          spinIcon={loading}
        >
          {t('common.commonButtons.save')}
        </Button>

        {isRoomMateSensor && (
          <>
            <Button
              color="primary"
              onClick={props.onRebootRequest}
              disabled={loading || !props.onRebootRequest || !isRoomMateSensor}
            >
              {t('manage.sensors.details.SensorAdvancedForm.restart')}
            </Button>

            <Button
              color="primary"
              onClick={props.onResetRequest}
              disabled={loading || !props.onResetRequest}
            >
              {t('manage.sensors.details.SensorAdvancedForm.reset')}
            </Button>
          </>
        )}
      </ButtonGroup>

      {isRoomMateSensor && (
        <Panel color="success" className="mt-3">
          <strong>
            {' '}
            {t('manage.sensors.details.SensorAdvancedForm.zeroing')}:
          </strong>{' '}
          {t('manage.sensors.details.SensorAdvancedForm.resetingInfo')}
        </Panel>
      )}
    </div>
  );
};
